import {
  Component,
  OnInit,
  Inject,
  ChangeDetectionStrategy
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AppService } from '../../../app.service';
import { CourseModuleModel } from '../../../models/course-module.model';
import { ApiRequestService } from '../../../shared/api-request.service';
import { UtilsService } from '../../../shared/utils.service';
import { RBACService } from '../../../shared/rbac.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

@Component({
  selector: 'app-courses-modules-edit',
  templateUrl: './courses-modules-edit.component.html',
  styleUrls: ['./courses-modules-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class CoursesModulesEditComponent implements OnInit {
  parent_id: number;

  module: CourseModuleModel = new CourseModuleModel();

  inProgress = false;

  // RBAC permission flags. Resolved once in ngOnInit() to avoid re-evaluating on every change detection cycle.
  canDelete: boolean = false;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    private data: { parent_id: number; module: CourseModuleModel },
    private dialogRef: MatDialogRef<CoursesModulesEditComponent>,
    public route: ActivatedRoute,
    public router: Router,
    public app: AppService,
    private api: ApiRequestService,
    public utils: UtilsService,
    public rbac: RBACService
  ) {}

  ngOnInit() {
    // Resolve RBAC permission flags.
    this.canDelete = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_ACCOUNT_INDUCTIONS,
          RBACService.RBAC_A.A_DELETE
        )
      ],
      true
    );

    this.parent_id = this.data.parent_id;
    this.module = this.data.module;
  }

  onSubmit(form: NgForm) {
    if (!form.valid) {
      this.utils.showModal(
        'Form Validation Error',
        'Please make sure you enter all fields with valid information.'
      );
      return;
    }

    if (this.inProgress) {
      return;
    }

    this.inProgress = true;

    if (this.module.id) {
      this.api
        .makeRequest(
          'put',
          `v1/inductions/${this.parent_id}/modules/${this.module.id}`,
          this.module
        )
        .then((response: any) => {
          this.utils.showToast('The module was updated.');
          this.dialogRef.close(true);
          this.inProgress = false;
        })
        .catch((err: any) => {
          this.inProgress = false;
        });
    } else {
      this.api
        .makeRequest(
          'post',
          `v1/inductions/${this.parent_id}/modules`,
          this.module
        )
        .then((response: any) => {
          this.utils.showToast('The module was created');
          this.dialogRef.close(true);
          this.inProgress = false;
        })
        .catch((err: any) => {
          this.inProgress = false;
        });
    }
  }

  onRemove() {
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_ACCOUNT_INDUCTIONS,
            RBACService.RBAC_A.A_DELETE
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to delete induction modules.'
      );
      return;
    }

    this.utils.showModal(
      'Remove Module',
      'Are you sure you want to remove this induction module?',
      () => {
        this.api
          .makeRequest(
            'delete',
            `v1/inductions/${this.parent_id}/modules/${this.module.id}`
          )
          .then((response: any) => {
            this.utils.showToast('The induction module is deleted.');
            this.router.navigate(['courses', this.parent_id, 'modules']);
          });
      }
    );
  }

  protected readonly RBACService = RBACService;
}
